<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useYardAssetFetcher } from '@satellite/composables/useYardAssetFetcher';
import { useYardAssetDisplay } from '@satellite/composables/useYardAssetDisplay';
import YardAssetDisplay from '@satellite/components/elements/presenters/YardAssetDisplay.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAssetDisplayField } from '@satellite/composables/useYardAssetDisplay';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';

const {
  size = 'regular',
  orientation = 'horizontal',
  assetId = null,
  initialAssetData = null,
  placeholderAssetData = null,
  staticAssetData = null,
  mode = EntityPresenterMode.Live,
  warehouseTimezone = null,
  displayFields = ['code'],
  assetCodeProps = {},
  arrivedTimeProps = {},
  assetStateProps = {},
  assetStatusProps = {},
  assetStatusIconProps = {},
  assetDisplayStatusProps = {},
  assetDisplayStatusIconProps = {},
  assetTypeChipProps = {},
  assetTypeIconProps = {},
  assetTypeProps = {},
  spotProps = {},
  stateIconProps = {},
  tagsProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  orientation?: 'horizontal' | 'vertical';
  assetId?: Maybe<string>;
  initialAssetData?: Maybe<YardAssetDetails>;
  placeholderAssetData?: Maybe<PresenterPlaceholderData<YardAssetDetails>>;
  staticAssetData?: Maybe<YardAssetDetails>;
  mode?: EntityPresenterMode;
  displayFields?: YardAssetDisplayField[];
  warehouseTimezone?: Maybe<string>;
  assetCodeProps?: Record<string, unknown>;
  arrivedTimeProps?: Record<string, unknown>;
  assetStateProps?: Record<string, unknown>;
  assetStatusProps?: Record<string, unknown>;
  assetStatusIconProps?: Record<string, unknown>;
  assetDisplayStatusProps?: Record<string, unknown>;
  assetDisplayStatusIconProps?: Record<string, unknown>;
  assetTypeChipProps?: Record<string, unknown>;
  assetTypeIconProps?: Record<string, unknown>;
  assetTypeProps?: Record<string, unknown>;
  spotProps?: Record<string, unknown>;
  stateIconProps?: Record<string, unknown>;
  tagsProps?: Record<string, unknown>;
}>();

const {
  data: asset,
  isLoading,
  isFetching,
  onIntersect
} = useYardAssetFetcher({
  assetId: computed(() => assetId),
  initialAssetData: computed(() => initialAssetData),
  placeholderAssetData: computed(() => placeholderAssetData),
  staticAssetData: computed(() => staticAssetData),
  mode
});

const data = useYardAssetDisplay(asset, {
  size: computed(() => size),
  warehouseTimezone: computed(() => warehouseTimezone),
  mode: computed(() => mode),
  isFetching,
  codeProps: computed(() => assetCodeProps),
  arrivedTimeProps: computed(() => arrivedTimeProps),
  stateProps: computed(() => assetStateProps),
  statusProps: computed(() => assetStatusProps),
  statusIconProps: computed(() => assetStatusIconProps),
  displayStatusProps: computed(() => assetDisplayStatusProps),
  displayStatusIconProps: computed(() => assetDisplayStatusIconProps),
  typeChipProps: computed(() => assetTypeChipProps),
  typeIconProps: computed(() => assetTypeIconProps),
  typeProps: computed(() => assetTypeProps),
  spotPresenterProps: computed(() => spotProps),
  stateIconProps: computed(() => stateIconProps),
  tagsContainerProps: computed(() => tagsProps)
});
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }"
    class="yard-asset-presenter-v2 bg-transparent">
    <yard-asset-display
      :data="data"
      :display-fields="displayFields"
      :orientation="orientation"
      :style="isLoading ? 'min-width: 50px' : ''">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </yard-asset-display>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
